import { NavLink, useLocation } from 'react-router-dom';
import { useAppSelector } from '@/app/hooks';
import { selectAuthStatus, selectCurrentUser } from '@mmef/shared/auth';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { AccountMenu } from './AccountMenu';

const linkClass = ({ isActive }: { isActive: boolean }) => `nav__link${isActive ? ' nav__link--active' : ''}`;

/**
 * Deliberately short.
 *
 * <p>The main links are the two things any visitor might want: browse events, or tell us about
 * one. Everything account-shaped — alerts, organiser links, saved events — lives on the account
 * page rather than competing for space up here, and the operator tools sit behind a single Admin
 * entry instead of putting Moderation and Analytics in front of people who can't use them.
 */
export function NavBar() {
  const user = useAppSelector(selectCurrentUser);
  const status = useAppSelector(selectAuthStatus);
  const location = useLocation();

  return (
    <header className="nav">
      <div className="nav__inner">
        <div className="nav__lockup">
          <NavLink to="/" className="nav__brand">
            Muslim Marriage Events
          </NavLink>
          {/* Not a heading and not a link — a description of the site, sitting where a strapline
              goes. It used to be the first paragraph of the events page, above the search box. */}
          <p className="nav__tagline">Muslim matrimonial events &amp; nikah meetups across the UK</p>
        </div>

        <nav className="nav__links" aria-label="Main">
          <NavLink to="/" className={linkClass} end>
            Events
          </NavLink>
          {/* Stays public: anyone can tell us about an event, signed in or not. */}
          <NavLink to="/submit" className={linkClass}>
            Submit an event
          </NavLink>
        </nav>

        <div className="nav__account">
          {/* Render nothing until the session check resolves — flashing "Sign in" at someone who
              is already signed in is worse than a moment of blank space. The sign-in link carries
              where they are now, so signing in returns them here rather than a default page. */}
          {status === 'unknown' ? null : user ? (
            <>
              {/* Account first, bell last: the bell is the thing you glance at, so it sits at the
                  very edge where the eye lands. Admin lives inside the menu now. */}
              <AccountMenu />
              <span className="nav__divider" aria-hidden="true" />
              <NotificationBell />
            </>
          ) : (
            <>
              {/* Both halves named, because there is no separate sign-up: the same link creates an
                  account for anyone who doesn't have one. "Sign in" alone sends a new visitor
                  looking for a button that was never going to exist. */}
              <NavLink to="/sign-in" state={{ from: location }} className="nav__link nav__link--cta">
                Sign in/Sign up
              </NavLink>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
